import AdminSidebar from "../../components/admin/AdminSidebar"
import AdminHeader from "../../components/admin/AdminHeader"
import { useNavigate } from "react-router-dom"
import { useWebinars } from "../../hooks/useWebinars"
import { Button } from "../../components/ui/button"
import { Plus, Edit, Trash2, Eye, Users } from "lucide-react"
import { useState, useEffect } from "react"
import { Badge } from "../../components/ui/badge"
import { formatDate, formatTime } from "../../utils/formatters"

export default function WebinarManagementPage() {
  const navigate = useNavigate()
  const { getAllWebinarsAdmin, deleteWebinar, loading } = useWebinars()
  const [webinars, setWebinars] = useState([])
  const [statusFilter, setStatusFilter] = useState("ALL")
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    loadWebinars()
  }, [statusFilter, page])

  const loadWebinars = async () => {
    try {
      const data = await getAllWebinarsAdmin({ status: statusFilter, page, limit: 20 })
      setWebinars(data?.data || [])
      setTotal(data?.total || 0)
      setTotalPages(data?.totalPages || 1)
    } catch (error) {
      console.error("Erreur lors du chargement des webinaires:", error)
    }
  }

  const handleCreateWebinar = () => {
    navigate("/admin/webinars/create")
  }

  const handleEditWebinar = (webinarId) => {
    navigate(`/admin/webinars/${webinarId}/edit`)
  }

  const handleViewWebinar = (webinarId) => {
    navigate(`/webinars/${webinarId}`)
  }

  const handleViewRegistrations = (webinarId) => {
    navigate(`/admin/webinars/${webinarId}/registrations`)
  }

  const handleDeleteWebinar = async (webinarId, webinarTitle) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le webinaire "${webinarTitle}" ? Cette action est irréversible.`)) {
      return
    }
    
    try {
      await deleteWebinar(webinarId)
      loadWebinars()
    } catch (error) {
      // L'erreur est déjà gérée dans le hook
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'UPCOMING':
        return { text: 'À venir', className: 'bg-blue-500 text-white' }
      case 'ONGOING':
        return { text: 'En cours', className: 'bg-green-500 text-white' }
      case 'COMPLETED':
        return { text: 'Terminé', className: 'bg-gray-500 text-white' }
      default:
        return { text: 'Non défini', className: 'bg-gray-400 text-white' }
    }
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <AdminHeader title="Gestion des Webinaires" />
        
        {/* Actions */}
        <div className="mb-6 flex justify-between items-center">
          <div className="flex gap-2">
            <Button
              variant={statusFilter === "ALL" ? "default" : "outline"}
              onClick={() => { setStatusFilter("ALL"); setPage(1) }}
              className={statusFilter === "ALL" ? "bg-[#203a8a] text-white" : ""}
            >
              Tous
            </Button>
            <Button
              variant={statusFilter === "UPCOMING" ? "default" : "outline"}
              onClick={() => { setStatusFilter("UPCOMING"); setPage(1) }}
              className={statusFilter === "UPCOMING" ? "bg-[#203a8a] text-white" : ""}
            >
              À venir
            </Button>
            <Button
              variant={statusFilter === "ONGOING" ? "default" : "outline"}
              onClick={() => { setStatusFilter("ONGOING"); setPage(1) }}
              className={statusFilter === "ONGOING" ? "bg-[#203a8a] text-white" : ""}
            >
              En cours
            </Button>
            <Button
              variant={statusFilter === "COMPLETED" ? "default" : "outline"}
              onClick={() => { setStatusFilter("COMPLETED"); setPage(1) }}
              className={statusFilter === "COMPLETED" ? "bg-[#203a8a] text-white" : ""}
            >
              Terminés
            </Button>
          </div>
          <Button
            onClick={handleCreateWebinar}
            className="bg-[#203a8a] text-white hover:bg-[#1a1f4a]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Créer un webinaire
          </Button>
        </div>

        {/* Liste des webinaires */}
        {loading && webinars.length === 0 ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#141835] mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des webinaires...</p>
          </div>
        ) : webinars.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg mb-4">Aucun webinaire disponible</p>
            <Button
              onClick={handleCreateWebinar}
              className="bg-[#203a8a] text-white hover:bg-[#1a1f4a]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer le premier webinaire
            </Button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Titre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Inscrits
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Publication
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {webinars.map((webinar) => {
                    const statusBadge = getStatusBadge(webinar.status)
                    const webinarDate = webinar.date ? new Date(webinar.date) : null
                    return (
                      <tr key={webinar.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {webinar.title}
                          </div>
                          {!webinar.isPublished && (
                            <Badge className="bg-yellow-500 text-white text-xs mt-1">
                              Brouillon
                            </Badge>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {webinarDate ? (
                            <div className="text-sm text-gray-900">
                              {formatDate(webinarDate)}
                              <br />
                              <span className="text-gray-500">{formatTime(webinarDate)}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">Non défini</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge className={statusBadge.className}>
                            {statusBadge.text}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {webinar.registrationsCount || 0}
                            {webinar.maxParticipants && ` / ${webinar.maxParticipants}`}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge className={webinar.isPublished ? "bg-green-500 text-white" : "bg-yellow-500 text-white"}>
                            {webinar.isPublished ? "Publié" : "Brouillon"}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewWebinar(webinar.id)}
                              title="Voir"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewRegistrations(webinar.id)}
                              title="Voir les inscrits"
                            >
                              <Users className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditWebinar(webinar.id)}
                              title="Modifier"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteWebinar(webinar.id, webinar.title)}
                              title="Supprimer"
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                >
                  Précédent
                </Button>
                <span className="flex items-center px-4">
                  Page {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                >
                  Suivant
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

